import React, { useEffect, useRef, useState } from 'react';

interface ConvertImageColorProps {
  base64Image: string;
}

const ConvertImageColor: React.FC<ConvertImageColorProps> = ({ base64Image }) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [parentWidth, setParentWidth] = useState(0);

  useEffect(() => {
    function updateSize() {
      if (canvasRef.current && canvasRef.current.parentElement) {
        setParentWidth(canvasRef.current.parentElement.offsetWidth || window.innerWidth);
      }
    }
    window.addEventListener('resize', updateSize);
    updateSize();
    return () => window.removeEventListener('resize', updateSize);
  }, []);

  useEffect(() => {
    const img = new Image();
    img.onload = function() {
      const canvas = canvasRef.current;
      if (!canvas) {
        return;
      }

      const ctx = canvas.getContext('2d');
      if (!ctx) {
        return;
      }

      if (parentWidth <= 0) {
        console.error('Parent width is 0, delaying image processing');
        return;
      }

      // Use natural dimensions for initial drawing
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;

      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

      // Adjust canvas size based on parent width, maintaining aspect ratio
      const scaleFactor = parentWidth / img.naturalWidth;
      canvas.width = img.naturalWidth * scaleFactor;
      canvas.height = img.naturalHeight * scaleFactor;
      ctx.scale(scaleFactor, scaleFactor);

      // Redraw image if scaling was necessary
      if (scaleFactor !== 1) {
        ctx.drawImage(img, 0, 0);
      }

      try {
        let imgData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        let pixels = imgData.data; // pixels in RGBA

        for (let i = 0; i < pixels.length; i += 4) {
          // Change all white (also shades of whites)
          // (pixels[i], pixels[i+1], pixels[i+2]) > 200 is a rough estimate for white and its shades
          if (pixels[i] > 200 && pixels[i + 1] > 200 && pixels[i + 2] > 200) {
            // change to your new color
            pixels[i] = 25; // Red component for #19bab4
            pixels[i + 1] = 186; // Green component for #19bab4
            pixels[i + 2] = 180; // Blue component for #19bab4
          }
          // change gray to #001236
          if (pixels[i] === pixels[i + 1] && pixels[i + 1] === pixels[i + 2]) {
            pixels[i] = 0; // Red component for #001236
            pixels[i + 1] = 18; // Green component for #001236
            pixels[i + 2] = 54; // Blue component for #001236
          }
        }

        // Put the manipulated data back to canvas
        ctx.putImageData(imgData, 0, 0);
      } catch (error) {
        console.error('Error manipulating image data:', error);
      }
    };

    img.src = base64Image;
  }, [base64Image, parentWidth]);

  return <canvas ref={canvasRef} style={{ width: '100%', height: 'auto' }} />;
};

export default ConvertImageColor;
